import html2canvas from "html2canvas";
import { PDFDocument } from "pdf-lib";
import { jsPDF } from "jspdf";
import { appendPoAnnexureVector, buildPoAnnexurePdfBlob, type PoAnnexurePdfInput } from "./poAnnexureVectorPdf";
import { fetchServerHtmlPdfBlob, isServerPdfUnavailable } from "./serverHtmlPdf";

/**
 * Browser PDF capture (html2canvas). Higher scale = sharper text; capped to avoid mobile OOM.
 * Server PDF (Chromium) is preferred when available — vector-sharp without this raster step.
 */
function pdfCanvasScale(): number {
  if (typeof window === "undefined") return 1.5;
  return Math.min(2, Math.max(1.5, window.devicePixelRatio || 1.5));
}

const IMAGE_WAIT_MS = 800;
const PDF_LAYOUT_TIMEOUT_MS = 45_000;
const LOGO_MAX_WIDTH_PX = 300;
const imageDataUrlCache = new Map<string, string>();

export type { PoAnnexurePdfInput };

export type HtmlToPdfOptions = {
  /** Fast vector annexure (issue PO — skips 2nd html2canvas). Same columns & data as HTML annexure. */
  annexure?: PoAnnexurePdfInput;
  /** Full HTML annexure (e.g. PO format sample) — slower. Ignored if `annexure` has rows. */
  annexureHtml?: string;
};

function baseFilename(name: string): string {
  return name.replace(/\.pdf$/i, "").trim() || "document";
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(r.error ?? new Error("read failed"));
    r.readAsDataURL(blob);
  });
}

async function imageBlobToDataUrl(blob: Blob): Promise<string> {
  if (!blob.type.startsWith("image/")) return blobToDataUrl(blob);

  try {
    const bitmap = await createImageBitmap(blob);
    const scale = Math.min(1, LOGO_MAX_WIDTH_PX / bitmap.width);
    const w = Math.max(1, Math.round(bitmap.width * scale));
    const h = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      bitmap.close();
      return blobToDataUrl(blob);
    }
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(bitmap, 0, 0, w, h);
    bitmap.close();
    return canvas.toDataURL("image/jpeg", 0.88);
  } catch {
    return blobToDataUrl(blob);
  }
}

/** Pre-fetch PO logos so PDF export skips cold network + decode. */
export async function warmPoPdfLogoCache(urls: string[]): Promise<void> {
  const origin = window.location.origin;
  await Promise.all(
    urls.map(async (raw) => {
      const t = raw.trim();
      if (!t || t.startsWith("data:")) return;
      try {
        const u = new URL(t, origin);
        if (u.origin !== origin || imageDataUrlCache.has(u.toString())) return;
        const res = await fetch(u.toString(), { credentials: "same-origin", cache: "force-cache" });
        if (!res.ok) return;
        const blob = await res.blob();
        imageDataUrlCache.set(u.toString(), await imageBlobToDataUrl(blob));
      } catch {
        /* ignore */
      }
    }),
  );
}

async function inlineSameOriginImages(doc: Document): Promise<void> {
  const origin = window.location.origin;
  const imgs = Array.from(doc.querySelectorAll<HTMLImageElement>("img[src]"));
  await Promise.all(
    imgs.map(async (img) => {
      const raw = (img.getAttribute("src") ?? "").trim();
      if (!raw || raw.startsWith("data:")) return;
      let fetchUrl: string;
      try {
        const u = new URL(raw, origin);
        if (u.origin !== origin) return;
        fetchUrl = u.toString();
      } catch {
        return;
      }
      const cached = imageDataUrlCache.get(fetchUrl);
      if (cached) {
        img.src = cached;
        return;
      }
      try {
        const res = await fetch(fetchUrl, { credentials: "same-origin", cache: "force-cache" });
        if (!res.ok) return;
        const blob = await res.blob();
        const dataUrl = await imageBlobToDataUrl(blob);
        imageDataUrlCache.set(fetchUrl, dataUrl);
        img.src = dataUrl;
      } catch {
        /* leave original src */
      }
    }),
  );
}

function waitForImages(doc: Document, timeoutMs: number): Promise<void> {
  const imgs = Array.from(doc.images);
  if (imgs.length === 0) return Promise.resolve();
  return Promise.all(
    imgs.map(
      (img) =>
        img.complete && img.naturalHeight > 0
          ? Promise.resolve()
          : new Promise<void>((resolve) => {
              const timer = window.setTimeout(resolve, timeoutMs);
              const done = () => {
                window.clearTimeout(timer);
                resolve();
              };
              img.addEventListener("load", done, { once: true });
              img.addEventListener("error", done, { once: true });
            }),
    ),
  ).then(() => undefined);
}

type FrameOpts = { iframeWidth: string; iframeMinHeight: string };

async function renderHtmlToCanvas(html: string, frame: FrameOpts): Promise<HTMLCanvasElement> {
  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.title = "pdf-render";
  iframe.style.cssText = `position:fixed;top:0;left:-9999px;width:${frame.iframeWidth};min-height:${frame.iframeMinHeight};border:0;visibility:hidden;`;
  document.body.appendChild(iframe);

  try {
    const idoc = iframe.contentDocument;
    const win = iframe.contentWindow;
    if (!idoc || !win) throw new Error("Could not prepare PDF renderer.");

    idoc.open();
    idoc.write(html);
    idoc.close();

    await new Promise<void>((resolve, reject) => {
      if (idoc.readyState === "complete") {
        resolve();
        return;
      }
      const timer = window.setTimeout(
        () => reject(new Error("PDF layout timed out — the document may be too large for this device.")),
        PDF_LAYOUT_TIMEOUT_MS,
      );
      win.onload = () => {
        window.clearTimeout(timer);
        resolve();
      };
    });

    await inlineSameOriginImages(idoc);
    await waitForImages(idoc, IMAGE_WAIT_MS);
    for (const img of Array.from(idoc.images)) {
      if (!img.naturalWidth || !img.naturalHeight) img.remove();
    }
    await new Promise<void>((r) => requestAnimationFrame(() => requestAnimationFrame(() => r())));

    const body = idoc.body;
    try {
      return await html2canvas(body, {
        scale: pdfCanvasScale(),
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
        imageTimeout: IMAGE_WAIT_MS,
        windowWidth: body.scrollWidth,
        windowHeight: body.scrollHeight,
        removeContainer: true,
      });
    } catch (e) {
      const detail = e instanceof Error ? e.message : String(e);
      throw new Error(`Browser PDF capture failed (${detail}).`);
    }
  } finally {
    iframe.remove();
  }
}

function appendCanvasToPdf(
  pdf: jsPDF,
  canvas: HTMLCanvasElement,
  orientation: "portrait" | "landscape",
  isFirstPage: boolean,
): void {
  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = pdf.internal.pageSize.getHeight();
  const canvasWidth = canvas.width;
  const canvasHeight = canvas.height;
  const scale = pdfWidth / canvasWidth;
  const pageHeightPx = Math.floor(pdfHeight / scale);

  const PAGE_MARGIN_TOP = 10;
  const PAGE_MARGIN_BOTTOM = 8;
  const PAGE_MARGIN_LEFT = 8;
  const PAGE_MARGIN_RIGHT = 8;
  const usablePagePx = Math.floor((pdfHeight - PAGE_MARGIN_TOP - PAGE_MARGIN_BOTTOM) / scale);
  const usableWidthMm = pdfWidth - PAGE_MARGIN_LEFT - PAGE_MARGIN_RIGHT;

  let srcY = 0;
  let first = isFirstPage;

  while (srcY < canvasHeight) {
    const remaining = canvasHeight - srcY;
    const maxSlicePx = first ? pageHeightPx : usablePagePx;
    const sliceHeight = remaining <= maxSlicePx ? remaining : maxSlicePx;

    const pageCanvas = document.createElement("canvas");
    pageCanvas.width = canvasWidth;
    pageCanvas.height = sliceHeight;
    const pctx = pageCanvas.getContext("2d")!;
    pctx.fillStyle = "#ffffff";
    pctx.fillRect(0, 0, canvasWidth, sliceHeight);
    pctx.drawImage(canvas, 0, srcY, canvasWidth, sliceHeight, 0, 0, canvasWidth, sliceHeight);

    const pageImg = pageCanvas.toDataURL("image/png");
    const sliceMmH = usableWidthMm * (sliceHeight / canvasWidth);

    if (!first) {
      pdf.addPage("a4", orientation === "landscape" ? "l" : "p");
    }
    pdf.addImage(pageImg, "PNG", PAGE_MARGIN_LEFT, PAGE_MARGIN_TOP, usableWidthMm, sliceMmH);

    srcY += sliceHeight;
    first = false;
  }
}

async function mergeSequentialPdfBlobs(parts: Blob[]): Promise<Blob> {
  const merged = await PDFDocument.create();
  for (const part of parts) {
    const doc = await PDFDocument.load(await part.arrayBuffer());
    const pages = await merged.copyPages(doc, doc.getPageIndices());
    pages.forEach((p) => merged.addPage(p));
  }
  const bytes = await merged.save();
  return new Blob([new Uint8Array(bytes)], { type: "application/pdf" });
}

export async function htmlDocumentToPdfBlob(html: string, options?: HtmlToPdfOptions): Promise<Blob> {
  const vec = options?.annexure;
  const useVectorAnnexure = Boolean(vec?.allocations?.length);
  const annexureHtml = options?.annexureHtml?.trim();

  const serverPortrait = await fetchServerHtmlPdfBlob(html, false);
  if (serverPortrait) {
    try {
      if (useVectorAnnexure && vec) {
        return await mergeSequentialPdfBlobs([serverPortrait, buildPoAnnexurePdfBlob(vec)]);
      }
      if (annexureHtml) {
        const serverAnnex = await fetchServerHtmlPdfBlob(annexureHtml, true);
        if (serverAnnex) {
          return await mergeSequentialPdfBlobs([serverPortrait, serverAnnex]);
        }
      } else {
        return serverPortrait;
      }
    } catch (e) {
      console.warn("[htmlDocumentToPdfBlob] server PDF failed, falling back to client capture:", e);
    }
  }

  const portraitFrame: FrameOpts = { iframeWidth: "816px", iframeMinHeight: "1100px" };
  const annexFrame: FrameOpts = { iframeWidth: "1100px", iframeMinHeight: "794px" };

  try {
    const portraitCanvas = await renderHtmlToCanvas(html, portraitFrame);

    const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4", compress: true });
    appendCanvasToPdf(pdf, portraitCanvas, "portrait", true);

    if (useVectorAnnexure && vec) {
      appendPoAnnexureVector(pdf, vec);
    } else if (annexureHtml) {
      const landscapeCanvas = await renderHtmlToCanvas(annexureHtml, annexFrame);
      appendCanvasToPdf(pdf, landscapeCanvas, "landscape", false);
    }

    const out = pdf.output("blob");
    if (!(out instanceof Blob)) throw new Error("PDF export did not produce a file.");
    return out;
  } catch (e) {
    const base = e instanceof Error ? e.message : "PDF export failed.";
    const serverHint = isServerPdfUnavailable()
      ? " Server PDF (Chromium) is not available on the API host."
      : "";
    throw new Error(
      `${base}${serverHint} Try a desktop browser, reduce PO size, or ask your admin to enable server PDF on the API server.`,
    );
  }
}

export function downloadPdfBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = `${baseFilename(filename)}.pdf`;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function downloadPdfFromHtml(html: string, filename: string, options?: HtmlToPdfOptions): Promise<void> {
  const blob = await htmlDocumentToPdfBlob(html, options);
  downloadPdfBlob(blob, filename);
}
